import { useEffect } from 'react'
import { useDispatch, useSelector } from '../../../store/hooks'
import { BoxV2, TableComponentV2 } from '@labrav/react-components'
import { useStyles } from './FlyOutMigrationProductsList.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faListCheck,
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { openAwsProductLinkOnBoarding } from '../../../common/utils/constants'
import { CircularProgress, Link, Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { HelpDrawer } from '@labrav/common'
import cloudStorageLogo from '../../../common/images/cloud_storage.svg'
import {
  MigrationProduct,
  MigrationProductsData,
  ProductVisibility,
} from '../../../modules/addEditProduct/productsInfo/reducer'
import { getFlyOutMigrationProductsData } from '../../../modules/addEditProduct/productsInfo/action'
import { CloudType } from '../../../types/index'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading,
  useAccountId,
  useCurrentCloudProvider,
} from '@labrav/common'

export type SortedProductsType = {
  id: string
  Name: JSX.Element
  EntityType: string
  EntityId: string
  EntityArn: string
  LastModifiedDate: string
  Visibility: ProductVisibility
}[]

export const FlyOutMigrationProductsList = ({
  isImpartner,
}: {
  isImpartner?: boolean
}) => {
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout', cloud: CloudType.AWS })
  const { classes } = useStyles()
  const selectedCloudMarketplace = useCurrentCloudProvider()
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS)
  )
  const products = useSelector<MigrationProductsData>(
    state => state.flyOutMigrationProductsInfo.migrationProducts.products
  )

  const productsMapping = {
    'Product ID': 'EntityId',
    'Product title': 'Name',
    Type: 'type',
    ...(selectedCloudMarketplace === CloudType.AWS
      ? { Status: 'Visibility' }
      : {}),
  }

  const { values, errors, setFieldValue, touched } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const migrationDetails = values.migrationDetails
  const migrationDetailsTouched = touched?.migrationDetails
  const migrationDetailsError = errors?.migrationDetails
  const selectedProductIds = migrationDetails?.products || []

  useEffect(() => {
    fetchMoreData()
  }, [accountId])

  const fetchMoreData = async () => {
    dispatch(startLoading(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS))
    try {
      if (accountId) {
        await dispatch(
          getFlyOutMigrationProductsData(accountId, selectedCloudMarketplace)
        )
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      dispatch(stopLoading(LoadingTypes.FLYOUT_PRODUCT_MIGRATION_PRODUCTS))
    }
  }

  const productsMappingDataObjects = (products?.rows || []).map(productRow => {
    const { id, name, link } = getProductdetailBasedOnCloud(
      productRow,
      selectedCloudMarketplace
    )
    return {
      ...productRow,
      id: id || '',
      Name: <ProductNameWithLink name={name} link={link} />,
      type: getProductType(productRow?.EntityType),
    }
  })
  const handleChange = (selected: string[]) => {
    setFieldValue('migrationDetails.products', selected)
  }
  const sortedProducts: SortedProductsType = sortProducts(
    selectedProductIds,
    productsMappingDataObjects
  )

  return (
    <BoxV2
      heading={'Select products to migrate'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '20px', height: '16px' }}
        />
      }
      secondaryActions={
        <HelpDrawer
          sectionName={
            isImpartner
              ? 'migratedListingProductsImpartner'
              : 'migratedListingProducts'
          }
          headerTitle="Help"
        />
      }
    >
      {loading ? (
        <div className={classes.loaderRootContainer}>
          <img src={cloudStorageLogo} className={classes.LogoImg} />
          <Typography
            data-testid="loading-header"
            className={classes.loaderHeader}
          >
            Loading your products for migration. Please wait...
          </Typography>
          <Typography
            data-testid="loading-sub-header"
            className={classes.loaderSubHeader}
          >
            Hold on. It may take 3 to 5 minutes.
          </Typography>
          <CircularProgress
            data-testid="circular-progress"
            className={classes.loadingSpinner}
          />
        </div>
      ) : (
        <TableComponentV2
          keyName={'migrationDetails.products'}
          objects={sortedProducts}
          mapping={productsMapping}
          count={products?.rows?.length || 0}
          fetchMoreData={() => void 0}
          selectedItem={migrationDetails.products}
          onChangeCheckbox={handleChange}
          tableHeight="calc(100vh - 400px)"
        />
      )}
      {!!migrationDetailsTouched?.products &&
        migrationDetailsError?.products && (
          <div className={classes.errorMessage}>
            {migrationDetailsError?.products}
          </div>
        )}
    </BoxV2>
  )
}

export const ProductNameWithLink = ({
  name,
  link,
}: {
  name?: string
  link: string
}) => {
  const { classes } = useStyles()
  return (
    <div className={classes.productName}>
      <div>{name || '-'}</div>
      <Link
        href={link}
        target="_blank"
        className={classes.productLink}
        data-testid="link-to-cloud-product-details"
      >
        <FontAwesomeIcon
          style={{ width: '14px', height: '14px' }}
          icon={faUpRightFromSquare}
        />
      </Link>
    </div>
  )
}

const getProductdetailBasedOnCloud = (
  product: MigrationProduct,
  cloudMarketplace: CloudMarketplace
) => {
  switch (cloudMarketplace) {
    case CloudType.AWS: {
      const pathname = `${openAwsProductLinkOnBoarding}${product.EntityId}`
      return { name: product.Name, id: product.EntityId, link: pathname }
    }
    case CloudType.AZURE: {
      const pathname = `${openAwsProductLinkOnBoarding}${product.id}`
      return { name: product?.alias, id: product?.id, link: pathname }
    }
    default:
      return { name: '-', id: '', link: '-' }
  }
}

export const sortProducts = (
  selectedProductIds: string[],
  productsMappingDataObjects: SortedProductsType
): SortedProductsType => {
  let sortedProducts: SortedProductsType = []

  if (selectedProductIds?.length > 0) {
    sortedProducts = [
      ...selectedProductIds
        ?.map(
          id =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (productsMappingDataObjects || []).find(
              product => product.id === id
            )!
        )
        .filter(Boolean),
      ...(productsMappingDataObjects || []).filter(
        product => !selectedProductIds?.includes(product.id)
      ),
    ]
  } else {
    sortedProducts = [...(productsMappingDataObjects || [])]
  }

  return sortedProducts
}

const getProductType = (entityType?: string) => {
  switch (entityType) {
    case 'SaaSProduct':
      return 'SaaS'
    case 'AmiProduct':
      return 'AMI'
    default:
      return entityType || '-'
  }
}
